import { Component, Input, computed, signal } from '@angular/core';
import { NgClass } from '@angular/common';

export type AppBadgeVariant =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral';
export type AppBadgeSize = 'sm' | 'md' | 'lg';
export type AppBadgeShape = 'rounded' | 'pill' | 'square';

/**
 * AppBadge - Componente de badge/status padronizado
 *
 * @example
 * ```html
 * <app-badge
 *   variant="success"
 *   size="md"
 *   shape="pill"
 *   [dot]="true">
 *   Ativo
 * </app-badge>
 * ```
 */
@Component({
  selector: 'app-badge',
  standalone: true,
  imports: [NgClass],
  template: `
    <span
      [ngClass]="computedClasses()"
      [attr.aria-label]="ariaLabel"
      role="status"
    >
      @if (dot()) {
      <span class="badge-dot" aria-hidden="true"></span>
      } @if (icon) {
      <i [ngClass]="'icon-' + icon" class="badge-icon" aria-hidden="true"></i>
      }

      <span class="badge-text">
        {{ text }}
        <ng-content></ng-content>
      </span>

      @if (removable()) {
      <button
        type="button"
        class="badge-remove"
        (click)="handleRemove($event)"
        aria-label="Remover badge"
      >
        <i class="icon-x" aria-hidden="true"></i>
      </button>
      }
    </span>
  `,
  styles: [
    `
      :host {
        @apply inline-block;
      }

      span {
        @apply inline-flex items-center gap-1 font-medium
        transition-all duration-200;
      }

      /* Variants */
      .variant-primary {
        @apply bg-primary-100 text-primary-800 border border-primary-200;
      }

      .variant-secondary {
        @apply bg-secondary-100 text-secondary-800 border border-secondary-200;
      }

      .variant-accent {
        @apply bg-accent-100 text-accent-800 border border-accent-200;
      }

      .variant-success {
        @apply bg-green-100 text-green-800 border border-green-200;
      }

      .variant-warning {
        @apply bg-yellow-100 text-yellow-800 border border-yellow-200;
      }

      .variant-error {
        @apply bg-red-100 text-red-800 border border-red-200;
      }

      .variant-info {
        @apply bg-blue-100 text-blue-800 border border-blue-200;
      }

      .variant-neutral {
        @apply bg-neutral-100 text-neutral-800 border border-neutral-200;
      }

      /* Sizes */
      .size-sm {
        @apply px-2 py-0.5 text-xs;
      }

      .size-md {
        @apply px-2.5 py-1 text-sm;
      }

      .size-lg {
        @apply px-3 py-1.5 text-base;
      }

      /* Shapes */
      .shape-rounded {
        @apply rounded-md;
      }

      .shape-pill {
        @apply rounded-full;
      }

      .shape-square {
        @apply rounded-none;
      }

      .badge-dot {
        @apply w-2 h-2 rounded-full bg-current opacity-75;
      }

      .badge-icon {
        @apply w-3 h-3;
      }

      .badge-text {
        @apply leading-none;
      }

      .badge-remove {
        @apply -mr-1 ml-1 p-0.5 rounded-full
        hover:bg-black hover:bg-opacity-10
        focus:outline-none focus:bg-black focus:bg-opacity-20
        transition-colors duration-150;
      }

      .badge-remove i {
        @apply w-3 h-3;
      }
    `,
  ],
})
export class AppBadgeComponent {
  // Signals para estado
  private _variant = signal<AppBadgeVariant>('neutral');
  private _size = signal<AppBadgeSize>('md');
  private _shape = signal<AppBadgeShape>('rounded');
  private _dot = signal(false);
  private _removable = signal(false);

  @Input() icon?: string;
  @Input() ariaLabel?: string;
  @Input() text?: string;

  // Enhanced inputs
  @Input({ required: false }) set badgeVariant(value: AppBadgeVariant) {
    this._variant.set(value);
  }

  @Input({ required: false }) set variant(value: AppBadgeVariant) {
    this._variant.set(value);
  }

  @Input({ required: false }) set badgeSize(value: AppBadgeSize) {
    this._size.set(value);
  }

  @Input({ required: false }) set badgeShape(value: AppBadgeShape) {
    this._shape.set(value);
  }

  @Input({ required: false }) set showDot(value: boolean) {
    this._dot.set(value);
  }

  @Input({ required: false }) set isRemovable(value: boolean) {
    this._removable.set(value);
  }

  // Getters for computed access
  get variant() {
    return this._variant();
  }
  size = computed(() => this._size());
  shape = computed(() => this._shape());
  dot = computed(() => this._dot());
  removable = computed(() => this._removable());

  // Computed classes
  computedClasses = computed(() => {
    return [
      `variant-${this.variant}`,
      `size-${this.size()}`,
      `shape-${this.shape()}`,
    ]
      .filter(Boolean)
      .join(' ');
  });

  handleRemove(event: MouseEvent): void {
    event.stopPropagation();
    // Emit custom event for removal
    const removeEvent = new CustomEvent('badgeRemoved', {
      detail: { badge: this },
      bubbles: true,
    });
    event.target?.dispatchEvent(removeEvent);
  }
}
